import { RouteComponentProps } from "react-router-dom";
import { CountyProjectContextProps, ProjectService, ProjectServiceAreaMap, ProjectServiceAreaMapSpecialty } from "../..";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { AttributionControl, GeoJSON, MapContainer, TileLayer, Tooltip, Popup, useMapEvents, LayersControl, LayerGroup, FeatureGroup } from 'react-leaflet';
import { LatLng, LatLngBounds, LeafletMouseEvent, PathOptions } from 'leaflet';
import { withCountyProjectContext } from "../../CountyProjectContext";
import { FilterType, RegionSetStatusType } from "../../enumerators";
import Legend from "./Legend";
import { RegionQueryShape } from "../RegionStatusFilter";
import Spinner from "./Spinner";
import { AdequacyQueryShape } from "../../CountyProjectContainer";
import { parseQuerystring } from "@app/shared/utility";
import GeographyService from "../../../geography/geographyService";

type MapsProps = CountyProjectContextProps & RouteComponentProps<unknown>;

interface GeoJSONified {
  geoJSON: GeoJSON.GeoJSON;
  projectServiceAreaMap: ProjectServiceAreaMap;
}

function CountyGapsMap(props: MapsProps) {
  const [loading, setLoading] = useState(true);
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState<LatLng>(new LatLng(38.93108963718345, -94.69176899703997)); // KC office is the default
  const [allCountiesBounds, setAllCountiesBounds] = useState<LatLngBounds>();
  const [allProjectServiceAreaMaps, setAllProjectServiceAreaMaps] = useState<GeoJSONified[]>(null);
  const [projectServiceAreaMaps, setProjectServiceAreaMaps] = useState<GeoJSONified[]>(null);
  const [selectedGeoJSON, setSelectedGeoJSON] = useState<GeoJSONified>(null);
  const [buckets, setBuckets] = useState<number[][]>();
  const [colors, setColors] = useState<string[]>();
  const isMounted = useRef(false);
  const projectService = new ProjectService();
  const geographyService = new GeographyService();
  const [removeLegend, setRemoveLegend] = useState(false);
  const [isMapDragging, setIsMapDragging] = useState(false);
  const [stateBorders, setStateBorders] = useState<GeoJSON.FeatureCollection>(null);  

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    }
  })

  useEffect(() => {   
    geographyService.getStateBorders().then((border) => {
      if (!!border) {
        const result = JSON.parse(border) as GeoJSON.FeatureCollection;
        setStateBorders(result);
      }
    });
  }, [])

  useEffect(() => {
    projectService.getProjectServiceAreaMapAsync(props.project.projectId, props.runConfiguration.projectRunConfigurationId, props.runConfiguration.isProjected || props.runConfiguration.isProjectedTermed, props.runConfiguration.isTermed || props.runConfiguration.isProjectedTermed)
      .then((serviceAreaMap) => {
        const allProjectServiceAreaMaps = serviceAreaMap.map(psam => geoJSONify(psam)).filter(f => !!f);

        setAllProjectServiceAreaMaps(allProjectServiceAreaMaps);
        if (allProjectServiceAreaMaps?.length === 0) {
          setLoading(false);
          return;
        }

        const allLatLngBounds = getLatLogBounds(allProjectServiceAreaMaps);
        setAllCountiesBounds(allLatLngBounds);

        const projectServiceAreaMaps = getFilteredProjectServiceAreaMapsByFilters(allProjectServiceAreaMaps);

        setProjectServiceAreaMaps(projectServiceAreaMaps);

        if (projectServiceAreaMaps.length === 0)
          setCenter(allLatLngBounds.getCenter())
        else {
          const latLngBounds = getLatLogBounds(projectServiceAreaMaps);
          setCenter(latLngBounds.getCenter());
        }

        setBuckets(getBuckets(serviceAreaMap[0].specialties.length));

        setLoading(false);
      })
  }, [props.runConfiguration])

  useEffect(() => {
    if (allCountiesBounds === undefined || allCountiesBounds === null)
      return;

    if (props.filters?.length > 0) {
      zoomToFilter();
      return;
    }

    map.fitBounds(allCountiesBounds);
  }, [allCountiesBounds])

  useEffect(() => {
    if (projectServiceAreaMaps === undefined || projectServiceAreaMaps === null)
      return;

    zoomToFilter();

  }, [props.filters])

  useEffect(() => {
    if (props.location.search === undefined || allProjectServiceAreaMaps === undefined || allProjectServiceAreaMaps === null || allProjectServiceAreaMaps.length == 0)
      return;

    const projectServiceAreaMaps = getFilteredProjectServiceAreaMapsByFilters(allProjectServiceAreaMaps);

    setProjectServiceAreaMaps(projectServiceAreaMaps);

    if (projectServiceAreaMaps.length === 0)
      map.fitBounds(allCountiesBounds);
    else if (selectedGeoJSON !== null) {
      setCountyView(selectedGeoJSON.projectServiceAreaMap);
    } else {
      const latLngBounds = getLatLogBounds(projectServiceAreaMaps)
      map.fitBounds(latLngBounds);
    }
  }, [props.history.location.search])

  const getFilteredProjectServiceAreaMapsByFilters = (allProjectServiceAreaMaps: GeoJSONified[]): GeoJSONified[] => {
    const query = parseQuerystring(props.location.search) as (RegionQueryShape & AdequacyQueryShape);
    const specialtyFilter = props.filters.find(f => f.filterType === FilterType.Specialty);

    const countyStatusFilters =
      query.active === true.toString() ||
      query.approved === true.toString() ||
      query.opportunity === true.toString() ||
      query.pending === true.toString();

    let projectServiceAreaMaps = allProjectServiceAreaMaps.filter(p => p.projectServiceAreaMap.status !== RegionSetStatusType.Opportunity);

    if (countyStatusFilters)
      projectServiceAreaMaps = allProjectServiceAreaMaps.filter(p =>
        (query.active === true.toString() && p.projectServiceAreaMap.status === RegionSetStatusType.Active)
        || (query.approved === true.toString() && p.projectServiceAreaMap.status === RegionSetStatusType.Approved)
        || (query.pending === true.toString() && p.projectServiceAreaMap.status === RegionSetStatusType.Pending)
        || (query.opportunity === true.toString() && p.projectServiceAreaMap.status === RegionSetStatusType.Opportunity));

    const gapExceptionFilter =
      query.gaps === true.toString() ||
      query.exceptions === true.toString();

    if (gapExceptionFilter)
      projectServiceAreaMaps = projectServiceAreaMaps.filter(p =>
        (query.gaps === true.toString() && p.projectServiceAreaMap.specialties.find(s => s.isGap === true && (!!specialtyFilter ? s.specialtyCode === specialtyFilter.filterValue : true)))
        || (query.exceptions === true.toString() && p.projectServiceAreaMap.specialties.find(s => s.hasException === true && (!!specialtyFilter ? s.specialtyCode === specialtyFilter.filterValue : true)))
      )

    setRemoveLegend(projectServiceAreaMaps == null || projectServiceAreaMaps.length === 0);

    return projectServiceAreaMaps;
  }

  const getLatLogBounds = (serviceAreas: GeoJSONified[]) => {
    const minLats = serviceAreas.map((gj) => gj.projectServiceAreaMap.minLat);
    const maxLats = serviceAreas.map((gj) => gj.projectServiceAreaMap.maxLat);
    const minLons = serviceAreas.map((gj) => gj.projectServiceAreaMap.minLon);
    const maxLons = serviceAreas.map((gj) => gj.projectServiceAreaMap.maxLon);

    const minLat = serviceAreas.length > 0 ? Math.min(...minLats) : -90.0;
    const maxLat = serviceAreas.length > 0 ? Math.max(...maxLats) : 90.0;
    const minLon = serviceAreas.length > 0 ? Math.min(...minLons) : -180.0;
    const maxLon = serviceAreas.length > 0 ? Math.max(...maxLons) : 180.0;

    return new LatLngBounds(new LatLng(maxLat, maxLon), new LatLng(minLat, minLon));
  }

  const zoomToFilter = () => {
    const state = props.filters.filter(f => f.filterType === FilterType.State);
    const county = props.filters.filter(f => f.filterType === FilterType.County);

    const serviceAreaMaps = getFilteredProjectServiceAreaMapsByFilters(allProjectServiceAreaMaps);
    setProjectServiceAreaMaps(serviceAreaMaps)

    if (state.length === 0 && county.length === 0) {
      map.fitBounds(allCountiesBounds);
      setSelectedGeoJSON(null);
      return;
    }

    if (county.length === 0) {
      const serviceAreas = serviceAreaMaps.filter(p => p.projectServiceAreaMap.state === state[0].filterValue);
      const latLngBounds = getLatLogBounds(serviceAreas)

      map.fitBounds(latLngBounds);
      setSelectedGeoJSON(null);
      return;
    }

    let serviceArea: GeoJSONified[];
    if (state.length === 0 && county.length !== 0)
      serviceArea = serviceAreaMaps.filter(p => p.projectServiceAreaMap.countySSA === county[0].filterValue);
    else
      serviceArea = serviceAreaMaps.filter(p => p.projectServiceAreaMap.state === state[0].filterValue && p.projectServiceAreaMap.countySSA === county[0].filterValue);

    if (serviceArea.length === 0)
      return;

    setCountyView(serviceArea[0].projectServiceAreaMap);
    setSelectedGeoJSON(serviceArea[0]);
  }

  const geoJSONify = (psam: ProjectServiceAreaMap) => {
    try {
      const geoJSON = JSON.parse(psam.geoJSON);
      return {
        geoJSON,
        projectServiceAreaMap: psam,
      } as GeoJSONified;
    }
    catch {
      return null;
    }
  }

  const navigateTo = (_selectedGeoJSON: GeoJSONified) => {

    const _selectedProjectServiceAreaMap = _selectedGeoJSON.projectServiceAreaMap;

    setSelectedGeoJSON(_selectedGeoJSON);

    let updatedPathname = props.location.pathname;

    // all the filters that need to be purged
    const removedFilters = props.filters.filter((f) => f.filterType === FilterType.County || f.filterType === FilterType.State);

    // remove the old filters from the updatedPathname
    removedFilters.forEach((f) => {
      updatedPathname = updatedPathname.replace(f.urlPart, "")
    });

    const stateFilter = {
      filterType: FilterType.State,
      filterValue: _selectedProjectServiceAreaMap.state,
      urlPart: `${FilterType.State}${encodeURIComponent(_selectedProjectServiceAreaMap.state)}`
    };

    const countyFilter = {
      filterType: FilterType.County,
      filterValue: _selectedProjectServiceAreaMap.countySSA,
      urlPart: `${FilterType.County}${encodeURIComponent(_selectedProjectServiceAreaMap.countySSA)}`
    };

    if (updatedPathname.endsWith('/'))
      updatedPathname = updatedPathname.slice(0, -1);

    updatedPathname = updatedPathname.concat(stateFilter.urlPart).concat(countyFilter.urlPart);

    props.history.push({
      ...props.history.location,
      pathname: updatedPathname
    });

    setCountyView(_selectedProjectServiceAreaMap);
  }

  const removeCountyFilter = () => {
    setSelectedGeoJSON(null);

    let updatedPathname = props.location.pathname;

    // Just remove county filter when closing out of a popup
    const removedFilters = props.filters.filter((f) => f.filterType === FilterType.County);

    removedFilters.forEach((f) => updatedPathname = updatedPathname.replace(f.urlPart, ""));

    props.history.push({
      ...props.history.location,
      pathname: updatedPathname
    })
  }

  const setCountyView = (projectServiceAreaMap: ProjectServiceAreaMap) => {
    const bound = new LatLngBounds(new LatLng(projectServiceAreaMap.maxLat, projectServiceAreaMap.maxLon), new LatLng(projectServiceAreaMap.minLat, projectServiceAreaMap.minLon))
    map.fitBounds(bound.pad(.5));
  }

  const getPathOptionsByGaps = (psam: ProjectServiceAreaMap): PathOptions => {
    const specialtyFilter = props.filters.find(f => f.filterType === FilterType.Specialty);
    const white = "#ffffff";
    if (!!specialtyFilter) {
      // filter to the selected specialty group
      const specialtyValues = psam.specialties.find(f => f.specialtyCode === specialtyFilter.filterValue);

      if (!!specialtyValues) {
        if (specialtyValues.isGap) {
          return {
            color: white,
            fillColor: colors[colors.length - 1],
            fillOpacity: 0.6,
            weight: 1,
          };
        }

        setRemoveLegend(projectServiceAreaMaps == null || projectServiceAreaMaps.length === 0);
      }
      else {
        setRemoveLegend(true);
      }

      return {
        color: white,
        fillColor: colors[0],
        fillOpacity: 0.6,
        weight: 1,
      };
    }
    else {
      // use county-level aggregates
      const gapSpecialtiesCount = psam.specialties.filter(f => f.isGap).length;

      if (gapSpecialtiesCount === 0)
        return {
          color: white,
          fillColor: colors[0],
          fillOpacity: 0.6,
          weight: 1,
        };

      const bucketIndex = buckets.findIndex(b => gapSpecialtiesCount <= b[1]);

      return {
        color: white,
        fillColor: colors[bucketIndex],
        fillOpacity: 0.6,
        weight: 1,
      };
    }
  }

  const getColors = (bucketCount: number) => {
    const darkestPurple = "#2d1742";
    const darkPurple = "#542a7d";
    const medDarkPurple = "#7b3eb8";
    const mediumPurple = "#a374d0";
    const medLightPurple = "#caafe4";
    const lightPurple = "#f1e9f8";
    if (bucketCount === 6)
      setColors([lightPurple, medLightPurple, mediumPurple, medDarkPurple, darkPurple, darkestPurple]);
    else if (bucketCount === 5)
      setColors([lightPurple, medLightPurple, mediumPurple, medDarkPurple, darkPurple]);
    else if (bucketCount === 4)
      setColors([lightPurple, mediumPurple, darkPurple, darkestPurple]);
    else if (bucketCount === 3)
      setColors([lightPurple, mediumPurple, darkestPurple]);
    else if (bucketCount === 2)
      setColors([lightPurple, darkestPurple]);
  }

  const makeTooltip = (psam: ProjectServiceAreaMap): JSX.Element => {

    const specialityFilter = props.filters.filter(f => f.filterType === FilterType.Specialty);

    let specialties: ProjectServiceAreaMapSpecialty[];

    if (specialityFilter.length != 0) {
      specialties = psam.specialties.filter(s => s.specialtyCode === specialityFilter[0].filterValue)
    }
    else {
      specialties = psam.specialties;
    }

    const county = `${psam.name}, ${psam.state}`;
    const countySSA = `${psam.countySSA}`;
    const countyClass = `${psam.countyClass}`;
    const countyStatus = `${psam.status}`;

    if (specialties.length === 0) {
      return (<>
        <table>
          <thead>
            <tr className="countyName">
              <td>{county}{psam.isPartial ? " (partial)" : ""}</td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>County SSA:</td>
              <td className="right-align">{countySSA}</td>
            </tr>
            <tr>
              <td>County Class:</td>
              <td className="right-align">{countyClass}</td>
            </tr>
            <tr>
              <td>County Status:</td>
              <td className="right-align">{countyStatus}</td>
            </tr>
            <tr>
              <td colSpan={2}>Gap density data is not available<br />for informational specialty groups.</td>
            </tr>
          </tbody>
        </table>
      </>)
    }
    else {
      const totalPossibleGaps = specialties.length;
      const totalGaps = specialties.filter(f => f.isGap)?.length;
      const accessGaps = specialties.filter(f => !f.meetsAccess)?.length;
      const servicingGaps = specialties.filter(f => !f.meetsServicing)?.length;
      const approvedExceptions = specialties.filter(f => f.isExceptionApproved)?.length;
      const totalExceptions = specialties.filter(f => f.hasException)?.length;

      // convert percentages to round to the nearest tenth decimal place
      const gapsPercent = Math.round(totalGaps / totalPossibleGaps * 1000) / 10;
      const accessPercent = Math.round(accessGaps / totalPossibleGaps * 1000) / 10;
      const servicingPercent = Math.round(servicingGaps / totalPossibleGaps * 1000) / 10;
      const approvedPercent = totalExceptions !== 0 ? Math.round(approvedExceptions / totalExceptions * 1000) / 10 : 0;

      return (<>
        <table>
          <thead>
            <tr className="countyName">
              <td>{county}{psam.isPartial ? " (partial)" : ""}</td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>County SSA:</td>
              <td> </td>
              <td> </td>
              <td className="right-align">{countySSA}</td>
            </tr>
            <tr>
              <td>County Class:</td>
              <td> </td>
              <td> </td>
              <td className="right-align">{countyClass}</td>
            </tr>
            <tr>
              <td>County Status:</td>
              <td> </td>
              <td> </td>
              <td className="right-align">{countyStatus}</td>
            </tr>
            <tr>
              <td>Total Gaps:</td>
              <td className="right-align">{totalGaps}</td>
              <td>of {totalPossibleGaps}</td>
              <td className="right-align">({gapsPercent}%)</td>
            </tr>
            <tr>
              <td>Servicing Gaps:</td>
              <td className="right-align">{servicingGaps}</td>
              <td>of {totalPossibleGaps}</td>
              <td className="right-align">({servicingPercent}%)</td>
            </tr>
            <tr>
              <td>Access Gaps:</td>
              <td className="right-align">{accessGaps}</td>
              <td>of {totalPossibleGaps}</td>
              <td className="right-align">({accessPercent}%)</td>
            </tr>
            {approvedExceptions > 0 &&
              <tr>
                <td>Approved Exceptions:</td>
                <td className="right-align">{approvedExceptions}</td>
                <td>of {totalExceptions}</td>
                <td className="right-align">({approvedPercent}%)</td>
              </tr>
            }
          </tbody>
        </table>
      </>)
    }
  }

  const getBuckets = (max: number, intervals = 5) => {
    const size = Math.round((max - 1) / intervals);
    const result = [];

    result.push([0, 0]);

    for (let i = 0; i < intervals; i++) {
      if (size === 0 && i === 0)
        continue;

      const inf = i + i * size;
      const sup = inf + size < max ? inf + size : max;

      result.push([inf === 0 ? 1 : inf, sup]);
      if (inf >= max || sup >= max)
        break;
    }

    getColors(result.length);

    return result;
  }

  const BordersGeoJSON = () => {

    return (
      <>
        <GeoJSON
          key={selectedGeoJSON.projectServiceAreaMap.countySSA}
          data={selectedGeoJSON.geoJSON}
          style={() => ({ className: "borderCounty" })}
          eventHandlers={{
            add: (event) => {
              const center = getLatLogBounds([selectedGeoJSON]).getCenter();
              const layer = event.target;
              layer.openPopup(center);
            },
            popupclose: () => {
              if (isMounted.current) {
                removeCountyFilter();
              }
            }
          }}
        >
          <Popup keepInView={true} autoPan={false}>
            {makeTooltip(selectedGeoJSON.projectServiceAreaMap)}
          </Popup>
        </GeoJSON>
      </>
    );
  };

  const bordersGeoJSON = useMemo(
    () => (
      <BordersGeoJSON />
    ), [selectedGeoJSON, props.filters])

  const DetectMapDragging = () => {
    useMapEvents({
      dragend: () => {
        setIsMapDragging(false);
      },
      dragstart: () => {
        setIsMapDragging(true);
      }
    });
    return (
      <>
      </>
    )
  }

  const getStateBorderStyle = (): PathOptions => {
    return {
      color: "grey", //"#DDC6C8",
      dashArray: '3, 3',
      fillOpacity: 0,
      opacity: 0.9,
      weight: 0.75,
    };
  }

  const displayMap = useMemo(
    () => (
      <MapContainer center={center} zoom={7} scrollWheelZoom={false} ref={setMap} attributionControl={false}>
        <Spinner loading={loading} />
        <DetectMapDragging />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          subdomains='abcd'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png"
          maxZoom={20} />

        <LayersControl position="topright">
          {!!stateBorders && 
            <LayersControl.Overlay name="State Borders" checked={!!projectServiceAreaMaps && !!stateBorders}>
              <LayerGroup>
                <FeatureGroup pathOptions={{ interactive: false }}>
                  {projectServiceAreaMaps && stateBorders && stateBorders.features.map((border: GeoJSON.GeoJSON, index: number) => (

                    <GeoJSON key={index} data={border} style={getStateBorderStyle()}>
                    </GeoJSON>
                  ))}

                </FeatureGroup>
              </LayerGroup>
            </LayersControl.Overlay>
          }
          <LayersControl.BaseLayer name="Counties" checked={!!projectServiceAreaMaps}>
            <LayerGroup>
              {projectServiceAreaMaps && projectServiceAreaMaps.map((gj) => (
                <GeoJSON key={gj.projectServiceAreaMap.countySSA} data={gj.geoJSON} style={getPathOptionsByGaps(gj.projectServiceAreaMap)}
                  eventHandlers={{
                    click: () => { navigateTo(gj) },
                    mousedown: (event: LeafletMouseEvent) => { event.originalEvent.preventDefault(); },
                    mouseover: (event) => {
                      if (!isMapDragging) {
                        const center = getLatLogBounds([gj]).getCenter();
                        const tooltip = event.target.getTooltip();
                        tooltip.setLatLng(center);
                      }
                    }
                  }}
                >
                  {!isMapDragging &&
                    <Tooltip direction="top">
                      {selectedGeoJSON ?
                        <strong>{`${gj.projectServiceAreaMap.name}, ${gj.projectServiceAreaMap.state}`}</strong> :
                        makeTooltip(gj.projectServiceAreaMap)
                      }
                    </Tooltip>
                  }
                </GeoJSON>
              ))}

              {selectedGeoJSON && bordersGeoJSON}
            </LayerGroup>
          </LayersControl.BaseLayer>
        </LayersControl>

        {buckets && <Legend buckets={buckets} colors={colors} bySpecialty={props.filters?.some(f => f.filterType === FilterType.Specialty)} removeLegend={removeLegend} />}
        <AttributionControl position="bottomright" prefix={false} />
      </MapContainer>
    ), [projectServiceAreaMaps, center, selectedGeoJSON, props.filters, isMapDragging, removeLegend, stateBorders])

  return (
    <div className={props.loading ? 'skeleton' : ''} >
      {displayMap}
    </div>
  )
}

const WrappedMaps = withCountyProjectContext(CountyGapsMap);

export { WrappedMaps as CountyGapsMap }